import { useMemo, useState } from 'react'
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts'
import { AlertTriangle, Layers, type LucideIcon } from 'lucide-react'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { shadesForBars, colorHex } from '../../lib/reporteColor'
import { formatNumero } from '../../lib/paisMapa'
import { parseFidelizacionError } from '../../hooks/useFidelizacionError'
import { parseNumeric, ejeCategoria } from './palette'
import { ChartDataTableDialog } from './ChartDataTableDialog'

export interface DatasetOpcion {
  key: string
  label: string
  icon?: LucideIcon
  /** `value2` es opcional: si se pasa (junto con `secondaryLabel` en Props) se
   *  dibuja una segunda barra agrupada por categoría. */
  data: Array<{ label: string; value: number | string; value2?: number | string }>
  isLoading?: boolean
  error?: unknown
  /** Indicador compacto opcional renderizado junto al título (cambia según opción activa). */
  headerStat?: { value: number | string; label: string }
}

interface Props {
  title: string
  options: DatasetOpcion[]
  colorNombre: string
  icon?: LucideIcon
  formatValue?: (v: number) => string
  /** @deprecated El chart ahora llena el alto del card (con tope max-h-[70vh]). */
  maxVisible?: number
  /** Callback cuando el usuario cambia de tab — útil para sincronizar estado externo (ej. el mapa). */
  onActiveChange?: (key: string) => void
  /** Etiqueta de la serie principal (barra 1). Default: 'Total'. */
  primaryLabel?: string
  /** Si se pasa, activa el modo de dos barras: cada categoría muestra
   *  `value` (primaria) y `value2` (secundaria) lado a lado. */
  secondaryLabel?: string
  /** Id de configuración para el formateo de números según el país del reporte. */
  configId?: number
  /** Si es true (modo dual), el tooltip muestra el % que representa la serie
   *  secundaria respecto a la primaria (ej. "% de participantes con puntos"). */
  secondaryPctOfPrimary?: boolean
}

const BAR_HEIGHT = 24

export function BarChartAgrupado({
  title,
  options,
  colorNombre,
  icon: Icon = Layers,
  formatValue,
  onActiveChange,
  primaryLabel = 'Total',
  secondaryLabel,
  configId,
  secondaryPctOfPrimary = false,
}: Props) {
  const dual = Boolean(secondaryLabel)
  const [active, setActive] = useState<string>(options[0]?.key ?? '')

  const handleSetActive = (key: string) => {
    setActive(key)
    onActiveChange?.(key)
  }

  const current = useMemo(
    () => options.find((o) => o.key === active) ?? options[0],
    [options, active],
  )

  const sorted = useMemo(() => {
    if (!current) return []
    return [...current.data]
      .map((d) => ({ ...d, _num: parseNumeric(d.value), _num2: parseNumeric(d.value2) }))
      .sort((a, b) => b._num - a._num)
  }, [current])

  // Eje de categorías: en el reporte 7 los labels se muestran completos (una
  // línea) ensanchando el eje; en el resto se trunca a 22 chars con ancho fijo.
  const eje = useMemo(
    () => ejeCategoria(sorted.map((d) => d.label), configId, { anchoFijo: 140, truncar: 22 }),
    [sorted, configId],
  )

  const shades = shadesForBars(colorNombre)
  // Colores fijos por serie en modo dual (2 barras); la secundaria ("con puntos")
  // resalta más fuerte que la primaria ("registrados"), que sirve de contexto.
  const primaryColor = colorHex(colorNombre, '300')
  const secondaryColor = colorHex(colorNombre, '600')

  const { chartData, chartConfig } = useMemo(() => {
    if (dual) {
      const cfg: ChartConfig = {
        valor: { label: primaryLabel, color: primaryColor },
        valor2: { label: secondaryLabel, color: secondaryColor },
      }
      const data = sorted.map((item) => ({
        categoria: item.label,
        valor: item._num,
        valor2: item._num2,
        displayName: eje.formatLabel(item.label),
      }))
      return { chartData: data, chartConfig: cfg }
    }
    const cfg: ChartConfig = { valor: { label: primaryLabel } }
    const data = sorted.map((item, index) => {
      const key = `cat-${index}`
      const ratio = sorted.length <= 1 ? 0 : index / (sorted.length - 1)
      const colorIdx = Math.round(ratio * (shades.length - 1))
      const color = shades[colorIdx]
      cfg[key] = { label: item.label, color }
      return {
        categoria: item.label,
        valor: item._num,
        displayName: eje.formatLabel(item.label),
        fill: `var(--color-${key})`,
      }
    })
    return { chartData: data, chartConfig: cfg }
  }, [sorted, shades, dual, primaryLabel, secondaryLabel, primaryColor, secondaryColor, eje])

  // Solo el reporte Medifarma Perú (id 7) expone la tabla de datos completa.
  const mostrarTabla =
    configId === 7 && !current?.isLoading && !current?.error && chartData.length > 0

  const header = (
    <CardHeader className="flex flex-row items-center space-y-0 pb-2 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <CardTitle className="text-sm font-medium truncate">{title}</CardTitle>
        {current?.headerStat && (
          <span className="ml-1 inline-flex items-baseline gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-muted/40 px-2 py-0.5 text-xs shrink-0">
            <span className="font-semibold text-black dark:text-white">{current.headerStat.value}</span>
            <span className="text-muted-foreground">{current.headerStat.label}</span>
          </span>
        )}
      </div>
      {dual && (
        <div className="ml-auto flex items-center gap-3 text-[10px] text-muted-foreground shrink-0">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: primaryColor }} />
            {primaryLabel}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: secondaryColor }} />
            {secondaryLabel}
          </span>
        </div>
      )}
      {mostrarTabla && (
        <span className={dual ? 'shrink-0' : 'ml-auto shrink-0'}>
          <ChartDataTableDialog
            title={current ? `${title} — ${current.label}` : title}
            rows={sorted}
            columns={[
              { header: 'Categoría', cell: (row) => row.label },
              {
                header: primaryLabel,
                align: 'right',
                cell: (row) =>
                  formatValue ? formatValue(row._num) : formatNumero(row._num, configId ?? 0),
              },
              ...(dual
                ? [
                    {
                      header: secondaryLabel as string,
                      align: 'right' as const,
                      cell: (row: (typeof sorted)[number]) =>
                        formatValue
                          ? formatValue(row._num2)
                          : formatNumero(row._num2, configId ?? 0),
                    },
                  ]
                : []),
            ]}
          />
        </span>
      )}
    </CardHeader>
  )

  const footer = (
    <CardFooter className="pt-0 pb-3 px-3 mt-auto">
      <Tabs value={active} onValueChange={handleSetActive} className="w-full">
        <TabsList className="w-full h-8">
          {options.map((opt) => {
            const OptIcon = opt.icon
            return (
              <TabsTrigger key={opt.key} value={opt.key} className="flex-1 h-7 px-2 text-xs gap-1">
                {OptIcon && <OptIcon className="h-3 w-3" />}
                {opt.label}
              </TabsTrigger>
            )
          })}
        </TabsList>
      </Tabs>
    </CardFooter>
  )

  if (!current) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700 flex flex-col h-full">
        {header}
        <CardContent className="flex-1">
          <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
            Sin datos disponibles
          </div>
        </CardContent>
        {footer}
      </Card>
    )
  }

  if (current.isLoading) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700 flex flex-col h-full">
        {header}
        <CardContent className="flex-1">
          <Skeleton className="h-[250px] w-full" />
        </CardContent>
        {footer}
      </Card>
    )
  }

  if (current.error) {
    const parsed = parseFidelizacionError(current.error)
    return (
      <Card className="border border-gray-300 dark:border-gray-700 flex flex-col h-full">
        {header}
        <CardContent className="flex-1">
          <div className="flex flex-col items-center justify-center gap-2 h-[250px] text-center px-4">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p className="text-sm font-medium">{parsed.title}</p>
            <p className="text-xs text-muted-foreground">{parsed.message}</p>
          </div>
        </CardContent>
        {footer}
      </Card>
    )
  }

  if (chartData.length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700 flex flex-col h-full">
        {header}
        <CardContent className="flex-1">
          <div className="flex items-center justify-center h-[250px] text-muted-foreground text-sm">
            Sin datos disponibles
          </div>
        </CardContent>
        {footer}
      </Card>
    )
  }

  // Alto mínimo del chart = una barra por dato (BAR_HEIGHT c/u). El contenedor
  // (flex-fill) lo estira vía minHeight:100% para llenar el card cuando hay pocas
  // barras; con muchas, este alto manda y aparece scroll alineado a la barra.
  const chartHeight = chartData.length * BAR_HEIGHT

  return (
    <Card className="border border-gray-300 dark:border-gray-700 flex flex-col h-full">
      {header}
      <CardContent className="flex-1 min-h-0">
        {/* Caja de tamaño fijo igual a la del mapa (aspect-ratio 460/340) para
            que el card quede del mismo alto que MapaRegiones en la misma fila;
            las barras que no caben se recorren con scroll interno. */}
        <div className="aspect-[460/340] w-full overflow-y-auto max-h-[70vh]">
          <ChartContainer
            config={chartConfig}
            style={{ height: `${chartHeight}px`, minHeight: '100%' }}
            className="w-full"
          >
            <BarChart
              accessibilityLayer
              data={chartData}
              layout="vertical"
              margin={{ left: 0, right: 60 }}
            >
              <YAxis
                dataKey="displayName"
                type="category"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                width={eje.width}
                tick={{ fontSize: 11 }}
                interval={0}
              />
              <XAxis dataKey="valor" type="number" hide />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    formatter={(value, name, item, index) => (
                      <div className="flex flex-col gap-0.5">
                        {(!dual || index === 0) && (
                          <span className="font-medium">{item.payload.categoria}</span>
                        )}
                        <span className="text-muted-foreground">
                          {dual ? `${chartConfig[name as string]?.label ?? name}: ` : ''}
                          {formatValue
                            ? formatValue(Number(value))
                            : formatNumero(Number(value), configId ?? 0)}
                          {secondaryPctOfPrimary &&
                            name === 'valor2' &&
                            Number(item.payload.valor) > 0 &&
                            ` (${formatNumero(
                              (Number(item.payload.valor2) / Number(item.payload.valor)) * 100,
                              configId ?? 0,
                              { maximumFractionDigits: 1 },
                            )}%)`}
                        </span>
                      </div>
                    )}
                    hideLabel
                  />
                }
              />
              {/* Recharts no detecta bien las <Bar> envueltas en un Fragment;
                  se renderizan como hijos hermanos con guardas. */}
              <Bar
                dataKey="valor"
                fill={dual ? 'var(--color-valor)' : undefined}
                layout="vertical"
                radius={dual ? 4 : 5}
              >
                <LabelList
                  dataKey="valor"
                  position="right"
                  className="fill-foreground"
                  fontSize={dual ? 11 : 12}
                  formatter={(value: number) =>
                    formatValue ? formatValue(value) : formatNumero(value, configId ?? 0)
                  }
                />
              </Bar>
              {dual && (
                <Bar dataKey="valor2" fill="var(--color-valor2)" layout="vertical" radius={4}>
                  <LabelList
                    dataKey="valor2"
                    position="right"
                    className="fill-foreground"
                    fontSize={11}
                    formatter={(value: number) =>
                      formatValue ? formatValue(value) : formatNumero(value, configId ?? 0)
                    }
                  />
                </Bar>
              )}
            </BarChart>
          </ChartContainer>
        </div>
      </CardContent>
      {footer}
    </Card>
  )
}
